import { Injectable } from '@angular/core';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { RemoteConfig, fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config';
import { environment } from 'src/environments/environment';
import { EnvironmentDirectory, normalizeEnvironmentDirectory } from 'src/app/shared/env-switcher';
import { FALLBACK_ENVIRONMENT_DIRECTORY } from 'src/environments/environment-directory-fallback';

const DEFAULT_MIN_SUPPORTED_VERSION = '0.0.0';

/**
 * Thin wrapper around firebase/remote-config (SHFT-5757). Reuses the same
 * Firebase app instance already created for Auth (FirebaseAuthService) -
 * initializeApp() is a no-op here if that app already exists.
 *
 * Consulted by BOTH the production and internal builds for maintenance_mode,
 * otp_url, and fm_web_min_supported_version. `environment_directory` is only
 * meaningful for the internal build's environment picker, but there's no harm
 * exposing it unconditionally.
 */
@Injectable({ providedIn: 'root' })
export class RemoteConfigService {
  private app: FirebaseApp;
  private remoteConfig: RemoteConfig;
  private readonly ready: Promise<void>;

  constructor() {
    this.app = getApps().length ? getApp() : initializeApp(environment.firebaseConfig);
    this.remoteConfig = getRemoteConfig(this.app);
    this.remoteConfig.defaultConfig = {
      environment_directory: JSON.stringify(FALLBACK_ENVIRONMENT_DIRECTORY),
      maintenance_mode: false,
      otp_url: environment.verifyOtpUrl,
      fm_web_min_supported_version: DEFAULT_MIN_SUPPORTED_VERSION
    };

    this.ready = fetchAndActivate(this.remoteConfig)
      .then(() => undefined)
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('RemoteConfigService: fetchAndActivate failed, using bundled/default values', err);
      });
  }

  /** Resolves once the initial fetch-and-activate attempt has settled (success or failure). */
  whenReady(): Promise<void> {
    return this.ready;
  }

  get environmentDirectory(): EnvironmentDirectory {
    try {
      const raw = getValue(this.remoteConfig, 'environment_directory').asString();
      const normalized = normalizeEnvironmentDirectory(JSON.parse(raw));
      if (normalized) {
        return normalized;
      }
    } catch {
      // fall through to bundled fallback
    }
    return FALLBACK_ENVIRONMENT_DIRECTORY;
  }

  get maintenanceMode(): boolean {
    return getValue(this.remoteConfig, 'maintenance_mode').asBoolean();
  }

  get otpUrl(): string {
    return getValue(this.remoteConfig, 'otp_url').asString() || environment.verifyOtpUrl;
  }

  get minSupportedVersion(): string {
    return getValue(this.remoteConfig, 'fm_web_min_supported_version').asString() || DEFAULT_MIN_SUPPORTED_VERSION;
  }
}
